import React, { useState, useEffect } from 'react';
import { fetchNews } from '../services/api';
import { NewspaperIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { NewsItem } from '../types/conference';

const NewsHighlights: React.FC = () => {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedNews, setSelectedNews] = useState<NewsItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  useEffect(() => {
    const loadNews = async () => {
      try {
        setLoading(true);
        const response = await fetchNews();
        
        // Extract news items from the response
        const rawNewsItems = response.news || [];
        
        // Convert backend field names to frontend field names
        const formattedNewsItems: NewsItem[] = rawNewsItems.map((item: any): NewsItem => ({
          id: item.id?.toString?.() ?? String(item.id),
          title: item.title ?? '',
          content: item.content ?? '',
          publishDate: item.published_date ?? new Date().toISOString().split('T')[0],
          author: item.author || 'Conference Team',
          category: item.category || 'announcement',
          featured: Boolean(item.featured),
          status: item.status === 'draft' ? 'draft' : 'published'
        }));
        
        // Get today's date at the start of the day (midnight) for comparison
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // Only show published news items with publish dates today or in the past
        const publishedNews = formattedNewsItems.filter((item: NewsItem) => 
          item.status === 'published' && new Date(item.publishDate) <= today
        );
        
        // Separate featured and non-featured news
        const featuredNews = publishedNews.filter((item: NewsItem) => item.featured);
        const nonFeaturedNews = publishedNews.filter((item: NewsItem) => !item.featured);
        
        // Sort by publish date (newest first)
        nonFeaturedNews.sort((a: NewsItem, b: NewsItem) => {
          return new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime();
        });
        
        // Combine: featured news first, then most recent non-featured news
        // Limit to 10 total items, but always include all featured items
        let newsToDisplay = [...featuredNews];
        const remainingSlots = Math.max(0, 10 - featuredNews.length);
        
        if (remainingSlots > 0) {
          newsToDisplay = [...newsToDisplay, ...nonFeaturedNews.slice(0, remainingSlots)];
        }
        
        setNews(newsToDisplay);
        setError(null);
      } catch (err) {
        console.error('Error fetching news:', err);
        setError('Could not load news items.');
      } finally {
        setLoading(false);
      }
    };

    loadNews();
  }, []);

  // If there are no news items or there was an error, don't render the component
  if ((news.length === 0 && !loading) || error) {
    return null;
  }
  
  const openNewsModal = (item: NewsItem) => {
    setSelectedNews(item);
    setIsModalOpen(true);
    // Prevent scrolling of the background when modal is open
    document.body.style.overflow = 'hidden';
  };
  
  const closeNewsModal = () => {
    setIsModalOpen(false);
    setSelectedNews(null);
    // Re-enable scrolling
    document.body.style.overflow = 'auto';
  };

  return (
    <section className="bg-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center">
            <NewspaperIcon className="h-8 w-8 text-[#00529B] mr-3" />
            <h2 className="text-3xl font-bold text-gray-900">Latest News</h2>
          </div>
          <Link 
            to="/news" 
            className="flex items-center text-[#00529B] hover:text-[#003D74] font-medium"
          >
            View all news
            <ChevronRightIcon className="ml-1 h-5 w-5" />
          </Link>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00529B]"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {news.map((item) => (
              <div key={item.id} className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">{item.title}</h3>
                    {item.featured && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 ml-2">
                        Featured
                      </span>
                    )}
                  </div>
                  
                  <div className="flex items-center text-sm text-gray-500 mb-3">
                    <span>{item.author}</span>
                    <span className="mx-2">•</span>
                    <span>{new Date(item.publishDate).toLocaleDateString()}</span>
                    {item.category && (
                      <>
                        <span className="mx-2">•</span>
                        <span className="capitalize">{item.category}</span>
                      </>
                    )}
                  </div>
                  
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {item.content
                      .replace(/<[^>]*>/g, '') // Remove HTML tags
                      .replace(/!\[.*?\]\(.*?\)/g, '') // Remove image syntax (do this before link syntax)
                      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Extract link text, discard URL
                      .replace(/[#*_~`]/g, '') // Remove remaining markdown syntax
                      .replace(/\n+/g, ' ') // Replace line breaks with spaces
                      .replace(/\s+/g, ' ') // Normalize multiple spaces to single space
                      .trim()
                    }
                  </p>
                  
                  <button
                    onClick={() => openNewsModal(item)}
                    className="text-[#00529B] hover:text-[#003D74] font-medium inline-flex items-center"
                  >
                    Read more
                    <ChevronRightIcon className="ml-1 h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* News Modal */}
      {isModalOpen && selectedNews && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="news-modal" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={closeNewsModal}></div>
            
            {/* Modal Panel */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                  onClick={closeNewsModal}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-2xl leading-6 font-bold text-gray-900 mb-4" id="modal-title">
                      {selectedNews.title}
                    </h3>
                    
                    <div className="flex items-center text-sm text-gray-500 mb-6">
                      <span>{selectedNews.author}</span>
                      <span className="mx-2">•</span>
                      <span>{new Date(selectedNews.publishDate).toLocaleDateString()}</span>
                      {selectedNews.category && (
                        <>
                          <span className="mx-2">•</span>
                          <span className="capitalize">{selectedNews.category}</span>
                        </>
                      )}
                      {selectedNews.featured && (
                        <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Featured
                        </span>
                      )}
                    </div>
                    
                    <div className="mt-2 prose prose-blue max-w-none">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {selectedNews.content}
                      </ReactMarkdown>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#00529B] sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={closeNewsModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default NewsHighlights;
